// Revoking a device from a journal (my space or a conversation): refuse its envelopes after the
// last one we know, then seal a fresh secret for every remaining device and switch epoch.

import type { Journal } from './journal';
import { newSpace, sealFor } from './keys';

/** Revokes several devices (a person's, when they leave a group) with a single key rotation. */
export async function revokeManyAndRekey(journal: Journal, ids: string[]): Promise<void> {
  const targets = ids.filter((id) => id !== journal.device.id && journal.members.has(id));
  if (!targets.length) return;
  for (const id of targets.slice(1)) {
    if (journal.members.get(id)?.revokedAfter === undefined) {
      await journal.append({ type: 'device.revoke', ts: Date.now(), device: id, afterSeq: journal.vector()[id] ?? 0 });
    }
  }
  await revokeAndRekey(journal, targets[0]);
}

export async function revokeAndRekey(journal: Journal, deviceId: string): Promise<void> {
  if (deviceId === journal.device.id || !journal.members.has(deviceId)) return;
  if (journal.members.get(deviceId)?.revokedAfter === undefined) {
    await journal.append({ type: 'device.revoke', ts: Date.now(), device: deviceId, afterSeq: journal.vector()[deviceId] ?? 0 });
  }
  const fresh = (await newSpace()).secrets[0];
  const epoch = journal.space.epoch + 1;
  const sealed: Record<string, { nonce: string; ct: string }> = {};
  for (const m of journal.activeMembers()) {
    if (m.record.id === journal.device.id) continue;
    const s = await sealFor(journal.device, m.record.dhPub, fresh);
    sealed[m.record.id] = { nonce: s.nonce, ct: s.ct };
  }
  await journal.append({ type: 'space.rekey', ts: Date.now(), epoch, sealed });
  await journal.adoptSecret(epoch, fresh, journal.device.id);
}
